<script setup lang="ts">
const { render, to, href, submit, size, testId } = defineProps<{
  testId?: string
  text?: string
  loading?: boolean
  disabled?: boolean
  to?: string
  href?: string
  iconStart?: string
  iconEnd?: string
  submit?: boolean
  render?: 'primary' | 'secondary' | 'link' | 'text' | 'special'
  size?: 'small' | 'large'
  block?: boolean
}>()
const ga4 = useAnalytics()
const component = (() => {
  if (to || href) return h(resolveComponent('NuxtLink'), { to, href })
  return h('button', { type: submit ? 'submit' : 'button' })
})()
const iconSize = (() => {
  return size === 'small' ? 22 : size === 'large' ? 26 : 24
})()
</script>

<template>
  <component
    :is="component" :disabled="disabled || loading" :data-test-id="`BaseButton|${testId || useKebabCase(text)}`"
    :class="[render || 'primary', size, block && 'w-full']" class="base-button duration-200 rounded-xl px-5 py-10px inline-flex justify-center items-center text-center font-medium"
    @click="ga4.buttonEvent({ event_action: 'button-click', event_label: testId || useKebabCase(text) })"
  >
    <AppIcon v-if="iconStart" class="mr-5px" :icon="iconStart" size="16" />
    <AppIcon v-if="loading" :size="iconSize" icon="fas-spinner" class="fa-spin" />
    <slot v-else>
      {{ text }}
    </slot>
    <AppIcon v-if="iconEnd" class="ml-5px" :icon="iconEnd" size="16" />
  </component>
</template>

<style>
  .base-button.small {
    @apply text-small px-14px py-7px rounded-lg;
  }

  .base-button.large {
    @apply text-large px-28px py-14px;
  }

  .base-button.primary {
    @apply bg-button text-white
    hover:bg-button-hover
    active:bg-button-active
    disabled:bg-gray disabled:text-white disabled:cursor-default;
  }

  .base-button.secondary {
    @apply text-secondary-active bg-secondary-active bg-opacity-15 cursor-pointer
    hover: (bg-opacity-30)
    active: (text-hover bg-opacity-45)
    disabled:text-gray-dark disabled:bg-gray disabled:bg-opacity-15 disabled:cursor-default;
  }

  .base-button.link {
    @apply text-secondary-active rounded-lg
    hover: (bg-secondary-active bg-opacity-10)
    active: (bg-opacity-30)
    disabled:text-gray-dark disabled:cursor-default;
  }

  .base-button.text {
    @apply text-primary px-0 py-0
    hover: (text-hover  underline-current underline underline-hover)
    disabled:text-gray-dark disabled:cursor-default;
  }
</style>
